import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {RequestStatus} from "../../utils/Enums";
import {apiClient} from "../../utils/ApiClient";
import {NewOrderFormProps} from "../../components/orders/NewOrderScreen";
//import {ModifyOrderFormProps} from "../../components/orders/ModifyOrderScreen";
import Order from "../../models/Order";
import {extractExtractedOrderSearchParams} from "../../utils/SearchHelpers";

interface OrdersState {
    orders: Order[];
    currentPage: number;
    totalPages: number;
    total: number;
    currentOrder?: Order | null;
    getOrdersStatus: RequestStatus;
    getCurrentOrder: RequestStatus;
    createOrderStatus: RequestStatus;
    updateOrderStatus: RequestStatus;
    deleteOrderStatus: RequestStatus;
    archiveOrderStatus: RequestStatus;
    sendInvoiceStatus: RequestStatus;
    createRatingStatus: RequestStatus;
    errors: string[];
}

const initialState: OrdersState = {
    orders: [],
    currentPage: 1,
    totalPages: 1,
    total: 0,
    currentOrder: null,
    getOrdersStatus: RequestStatus.Idle,
    getCurrentOrder: RequestStatus.Idle,
    createOrderStatus: RequestStatus.Idle,
    updateOrderStatus: RequestStatus.Idle,
    deleteOrderStatus: RequestStatus.Idle,
    archiveOrderStatus: RequestStatus.Idle,
    sendInvoiceStatus: RequestStatus.Idle,
    createRatingStatus: RequestStatus.Idle,
    errors: [],
};

export const getOrders = createAsyncThunk(
    'orders/getOrders',
    async (_, {getState, rejectWithValue}) => {
        const {search} = window.location;
        const urlParams = new URLSearchParams(search);
        const currentPage = urlParams.get('p') || 1;

        const queryString = extractExtractedOrderSearchParams();

        try {
            const response = await apiClient.get(`orders?page=${currentPage}&${queryString}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const getOrdersAll = createAsyncThunk(
    'orders/getOrdersAll',
    async (_, {rejectWithValue}) => {
        const response = await apiClient.get('orders/all');
        return response.data;
    }
);

const createFormData = async (values: Record<string, any>) => {
    const formData = new FormData();

    const appendFormData = async (data: any, prefix: string) => {
        if (data === null || data === 'null') {
            formData.append(prefix, ''); // Append empty string for null or 'null' values
        } else if (Array.isArray(data)) {
            if (prefix === 'loadings' || prefix === 'unloadings') {
                const dataArray = data.map(item => {
                    if (item.deleted === true) {
                        return {...item};
                    }
                    if (item.contacts) {
                        const updatedContacts = item.contacts.filter((contact: any) => contact.deleted !== true);
                        return {...item, contacts: updatedContacts};
                    }
                    return {...item};
                });
                formData.append(prefix, JSON.stringify(dataArray));
            } else {
                await Promise.all(data.map((item, i) => appendFormData(item, `${prefix}[${i}]`)));
            }
        } else if (typeof data === 'object' && data !== null) {
            if (data instanceof File) {
                formData.append(prefix || 'file', data);
            } else if (data instanceof Date) {
                const formattedDate = `${data.getDate()}.${data.getMonth() + 1}.${data.getFullYear()}`;
                formData.append(prefix, formattedDate);
            } else {
                await Promise.all(Object.entries(data).map(([key, value]) => appendFormData(value, `${prefix ? `${prefix}.${key}` : key}`)));
            }
        } else {
            if (prefix !== undefined) {
                formData.append(prefix, data);
            }
        }
    };

    await Promise.all(Object.entries(values).map(([key, value]) => appendFormData(value, key)));
    return formData;

};

export const createOrder = createAsyncThunk(
    'orders/createOrder',
    async ({values, id, type}: { values: NewOrderFormProps; id: number, type: string }, {rejectWithValue}) => {
        const formData = await createFormData(values);

        const url = (type === 'parent') ? `orders?order=${id}` : `orders?query=${id}`;

        const response = await apiClient.upload(url, formData);
        return response.data;
    }
);

export const getCurrentOrder = createAsyncThunk(
    'orders/getCurrentOrder',
    async ({id}: { id: string | undefined }, {rejectWithValue}) => {
        const response = await apiClient.get(`orders/${id}`);
        return response.data;
    }
);

export const updateOrder = createAsyncThunk(
    'orders/updateOrder',
    async ({id, data}: { id: string | undefined, data: any }, {rejectWithValue}) => {
        const formData = await createFormData(data);

        const response = await apiClient.upload(`orders/update/${id}`, formData);
        return response.data;
    }
);

export const deleteOrder = createAsyncThunk(
    'orders/deleteOrder',
    async ({id}: { id: string | undefined }, {rejectWithValue}) => {
        const response = await apiClient.delete(`orders/${id}`);
        return response.data;
    }
);

export const sendInvoice = createAsyncThunk(
    'orders/sendInvoice',
    async ({id}: { id: string | undefined }, {rejectWithValue}) => {
        const response = await apiClient.post(`orders/${id}/invoice`);
        return response.data;
    }
);

export const archiveOrder = createAsyncThunk(
    'orders/archiveOrder',
    async ({id}: { id: string | undefined }, {rejectWithValue}) => {
        const response = await apiClient.get(`orders/${id}/archive`);
        return response.data;
    }
);

export const deleteOrderFile = createAsyncThunk(
    'orders/deleteOrderFile',
    async ({id}: { id: number | undefined }, {rejectWithValue}) => {
        const response = await apiClient.get(`delete-file/${id}`);
        return response.data;
    }
);

export const createRating = createAsyncThunk(
    'orders/createRating',
    async ({id, rating, comment}: { id: string; rating: number; comment: string }, {rejectWithValue}) => {
        const response = await apiClient.post(`orders/${id}/rating`, {
            rating,
            comment
        });
        return response.data;
    }
);

const ordersSlice = createSlice({
    name: 'orders',
    initialState,
    reducers: {
        resetCreateOrderStatus: (state) => {
            state.createOrderStatus = RequestStatus.Idle;
            state.currentOrder = null;
        },
        resetUpdateOrderStatus: (state) => {
            state.updateOrderStatus = RequestStatus.Idle;
            state.currentOrder = null;
        },
        resetDeleteOrderStatus: (state) => {
            state.deleteOrderStatus = RequestStatus.Idle;
            state.currentOrder = null;
        },
        resetArchiveOrderStatus: (state) => {
            state.archiveOrderStatus = RequestStatus.Idle;
            state.currentOrder = null;
        },
        resetSentInvoicOrderStatus: (state) => {
            state.sendInvoiceStatus = RequestStatus.Idle;
            state.currentOrder = null;
        },
        resetCreateRatingStatus:(state) => {
            state.createRatingStatus = RequestStatus.Idle;
        },
        clearCurrentOrder: (state) => {
            state.currentOrder = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getOrders.pending, (state, action) => {
                state.getOrdersStatus = RequestStatus.Loading;
            })
            .addCase(getOrders.fulfilled, (state, action) => {
                state.getOrdersStatus = RequestStatus.Success;
                state.orders = action.payload.data;
                state.currentPage = action.payload.current_page;
                state.totalPages = action.payload.last_page;
                state.total = action.payload.total;
            })
            .addCase(getOrders.rejected, (state, action) => {
                state.getOrdersStatus = RequestStatus.Failed;
            })

            .addCase(createOrder.pending, (state, action) => {
                state.createOrderStatus = RequestStatus.Loading;
                state.errors = [];
            })
            .addCase(createOrder.fulfilled, (state, action) => {
                state.createOrderStatus = RequestStatus.Success;
            })
            .addCase(createOrder.rejected, (state, action) => {
                state.createOrderStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })

            .addCase(updateOrder.pending, (state, action) => {
                state.updateOrderStatus = RequestStatus.Loading;
                state.errors = [];
            })
            .addCase(updateOrder.fulfilled, (state) => {
                state.updateOrderStatus = RequestStatus.Success;
            })
            .addCase(updateOrder.rejected, (state, action) => {
                state.updateOrderStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })

            .addCase(deleteOrder.pending, (state, action) => {
                state.deleteOrderStatus = RequestStatus.Loading;
            })
            .addCase(deleteOrder.fulfilled, (state, action) => {
                state.deleteOrderStatus = RequestStatus.Success;
            })
            .addCase(deleteOrder.rejected, (state, action) => {
                state.deleteOrderStatus = RequestStatus.Failed;
            })

            .addCase(getCurrentOrder.pending, (state, action) => {
                state.getCurrentOrder = RequestStatus.Loading;
                state.currentOrder = null;
            })
            .addCase(getCurrentOrder.fulfilled, (state, action) => {
                state.getCurrentOrder = RequestStatus.Success;
                state.currentOrder = action.payload;
            })
            .addCase(getCurrentOrder.rejected, (state, action) => {
                state.getCurrentOrder = RequestStatus.Failed;
            })

            .addCase(getOrdersAll.pending, (state) => {
                state.getOrdersStatus = RequestStatus.Loading;
            })
            .addCase(getOrdersAll.fulfilled, (state, action) => {
                state.getOrdersStatus = RequestStatus.Success;
                state.orders = action.payload;
            })
            .addCase(getOrdersAll.rejected, (state, action) => {
                state.getOrdersStatus = RequestStatus.Failed;
            })

            .addCase(archiveOrder.pending, (state, action) => {
                state.archiveOrderStatus = RequestStatus.Loading;
            })
            .addCase(archiveOrder.fulfilled, (state, action) => {
                state.archiveOrderStatus = RequestStatus.Success;
            })
            .addCase(archiveOrder.rejected, (state, action) => {
                state.archiveOrderStatus = RequestStatus.Failed;
            })

            .addCase(sendInvoice.pending, (state, action) => {
                state.sendInvoiceStatus = RequestStatus.Loading;
            })
            .addCase(sendInvoice.fulfilled, (state, action) => {
                state.sendInvoiceStatus = RequestStatus.Success;
            })
            .addCase(sendInvoice.rejected, (state, action) => {
                state.sendInvoiceStatus = RequestStatus.Failed;
            })

            .addCase(createRating.pending, (state, action) => {
                state.createRatingStatus = RequestStatus.Loading;
            })
            .addCase(createRating.fulfilled, (state, action) => {
                state.createRatingStatus = RequestStatus.Success;
            })
            .addCase(createRating.rejected, (state, action) => {
                state.createRatingStatus = RequestStatus.Failed;
            })

    },
});

export const {
    resetCreateOrderStatus,
    resetUpdateOrderStatus,
    resetDeleteOrderStatus,
    resetArchiveOrderStatus,
    resetSentInvoicOrderStatus,
    resetCreateRatingStatus,
    clearCurrentOrder
} = ordersSlice.actions;
export default ordersSlice.reducer;
